import type { BotProductType } from '../hooks/useBotAgentAvailability';

const CANONICAL_ORDER: BotProductType[] = [
  'flight',
  'hotel',
  'package',
  'activity',
  'event',
];

/**
 * Builds the bot empty-state subtitle, honoring a per-tenant product
 * restriction (`enabled_products` from /bot-agent/availability).
 *
 * - Unrestricted (null, or all five products) → the full translated subtitle.
 * - Restricted → translated `subtitlePrefix` + the enabled product nouns
 *   (from the translated `productNoun` map), so the bot never advertises a
 *   product it can't actually deliver.
 *
 * All text comes from the translation block — nothing is hardcoded here beyond
 * a defensive English fallback if a key is missing.
 */
export function buildEmptyStateSubtitle(
  emptyState: any,
  enabledProducts: BotProductType[] | null | undefined,
): string {
  const fullSubtitle =
    emptyState?.subtitle ??
    'Ask me about flights, hotels, packages, or activities.';

  if (!enabledProducts || enabledProducts.length >= CANONICAL_ORDER.length) {
    return fullSubtitle;
  }

  const nounMap = emptyState?.productNoun ?? {};
  const nouns = CANONICAL_ORDER.filter((p) => enabledProducts.includes(p))
    .map((p) => nounMap[p])
    .filter(Boolean);

  if (nouns.length === 0) {
    return fullSubtitle;
  }

  const prefix = emptyState?.subtitlePrefix ?? 'Ask me about ';
  return `${prefix}${nouns.join(', ')}.`;
}
